import { ReactNode } from 'react';

import {
  Box,
  Checkbox,
  ChoicePanel,
  Stack,
  Typography,
  tokens,
} from '@cast/design-system';

type SelectableCardProps<T extends string> = {
  value: T;
  selected: boolean;
  onChange: (value: T) => void;
  title: string;
  titleSuffix?: ReactNode;
  description?: string;
  icon?: ReactNode;
  showCheckbox?: boolean;
  disabled?: boolean;
  testId?: string;
};

export const SelectableCard = <T extends string>({
  value,
  selected,
  onChange,
  title,
  titleSuffix,
  description,
  icon,
  showCheckbox,
  disabled,
  testId,
}: SelectableCardProps<T>) => {
  return (
    <ChoicePanel
      selected={selected}
      onClick={() => onChange(value)}
      disabled={disabled}
      testId={testId}
      sx={{
        position: 'relative',
        width: '100%',
        height: '100%',
        overflow: 'visible',
        p: 16,
      }}
    >
      {showCheckbox && (
        <Box sx={{ position: 'absolute', top: 8, right: 8 }}>
          <Checkbox
            checked={selected}
            disabled={disabled}
            disableRipple
            tabIndex={-1}
          />
        </Box>
      )}
      <Stack gap={12}>
        {icon && (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-start',
              color: tokens.theme.fg.structural.primary,
            }}
          >
            {icon}
          </Box>
        )}
        <Stack gap={6}>
          <Stack direction="row" alignItems="center" gap={12}>
            <Typography
              variant="h6"
              color={
                disabled
                  ? tokens.theme.disabled.fg
                  : tokens.theme.fg.structural.primary
              }
            >
              {title}
            </Typography>
            {titleSuffix}
          </Stack>
          {description && (
            <Typography
              variant="P12R"
              color={
                disabled
                  ? tokens.theme.disabled.fg
                  : tokens.theme.fg.structural.secondary
              }
            >
              {description}
            </Typography>
          )}
        </Stack>
      </Stack>
    </ChoicePanel>
  );
};
